
import { useState, useEffect } from "react";
import axios from "axios";
import { FaEdit, FaTrash, FaEye, FaTimes } from "react-icons/fa";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import api from '../../../Axios'


export default function ManageCommittees() {
  const [committees, setCommittees] = useState([]);
  const [activeCommitteeIndex, setActiveCommitteeIndex] = useState(0);
  const [editMember, setEditMember] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [viewMember, setViewMember] = useState(null);

  const API_BASE_URL = `${process.env.REACT_APP_BASE_URL}/api/v1/committees`;

  useEffect(() => {
    const fetchCommittees = async () => {
      setIsLoading(true);
      try {
        const response = await api.get(API_BASE_URL);
        const committeeData = response.data || [];
        setCommittees(committeeData);
        if (committeeData.length > 0) setActiveCommitteeIndex(0);
      } catch (error) {
        console.error("Error fetching committees:", error);
        toast.error("Failed to load committees. Please check your connection.");
      } finally {
        setIsLoading(false);
      }
    };

    fetchCommittees();
  }, []);

  const handleEditSubmit = async (e) => {
    e.preventDefault();

    if (!editMember) {
      toast.error("No member selected for editing.");
      return;
    }

    const { _id } = editMember;
    const currentCommittee = committees[activeCommitteeIndex];

    const currentMember = currentCommittee?.members.find(
      (member) => member._id === _id
    );

    if (!currentMember) {
      toast.error("Member not found!");
      return;
    }

    const updatedMemberData = {
      ...currentMember,
      ...editMember,
    };

    const updatedCommittees = committees.map((committee, index) =>
      index === activeCommitteeIndex
        ? {
          ...committee,
          members: committee.members.map((member) =>
            member._id === _id ? updatedMemberData : member
          ),
        }
        : committee
    );
    setCommittees(updatedCommittees);

    try {
      setIsLoading(true);
      await api.put(
        `${API_BASE_URL}/${currentCommittee._id}/members/${_id}`,
        updatedMemberData
      );

      toast.success("Member updated successfully!");
      closeModal(); // Close the modal automatically after a successful update
    } catch (error) {
      console.error("Error updating member:", error);
      toast.error("Failed to update member. Please try again.");
      setCommittees(committees);
    } finally {
      setIsLoading(false);
    }
  };

  const handleView = async (memberId) => {
    try {
      setIsLoading(true);
      const response = await api.get(
        `${API_BASE_URL}/${committees[activeCommitteeIndex]._id}/members/${memberId}`
      );
      const memberDetails = response.data;
      setViewMember(memberDetails);
      toast.info("Member details fetched successfully!");
    } catch (error) {
      console.error("Error viewing member details:", error);
      toast.error("Failed to fetch member details.");
    } finally {
      setIsLoading(false);
    }
  };

  const handleDelete = async (memberId) => {
    try {
      setIsLoading(true);
      await api.delete(
        `${API_BASE_URL}/${committees[activeCommitteeIndex]._id}/members/${memberId}`
      );

      setCommittees((prevCommittees) =>
        prevCommittees.map((committee, idx) =>
          idx === activeCommitteeIndex
            ? {
              ...committee,
              members: committee.members.filter((m) => m._id !== memberId),
            }
            : committee
        )
      );

      toast.success("Member deleted successfully!");
    } catch (error) {
      console.error("Error deleting member:", error);
      toast.error("Failed to delete member.");
    } finally {
      setIsLoading(false);
    }
  };

  const closeModal = () => {
    setViewMember(null);
    setEditMember(null);
  };

  return (
    // <div className="container mx-auto p-4 space-y-6 sm:p-6 md:p-8 lg:p-10 xl:p-12">
    <div className="container mx-auto px-4 py-6 space-y-8">
      <h1 className="text-3xl font-bold text-gray-800">Manage Committees</h1>
      {isLoading && <p>Loading...</p>}

      <div className="flex flex-wrap sm:flex-nowrap sm:space-x-4 overflow-x-auto mb-4">
        {committees.map((committee, index) => (
          <button
            key={committee._id}
            onClick={() => setActiveCommitteeIndex(index)}
            className={`px-4 py-2 rounded-lg mb-2 sm:mb-0 ${activeCommitteeIndex === index
              ? "bg-teal-500 text-white"
              : "bg-gray-200 text-gray-700"
              }`}
          >
            {committee.title}
          </button>
        ))}
      </div>

      {committees[activeCommitteeIndex] && (
        <div>
          <h2 className="text-2xl font-semibold text-gray-700 mb-4">
            {committees[activeCommitteeIndex].title}
          </h2>
          <div className="overflow-x-auto">
            <table className="min-w-full table-auto border-collapse mt-4">
              <thead>
                <tr className="bg-gray-100">
                  <th className="px-2 py-3 text-left text-xs sm:text-sm font-semibold">Name</th>
                  <th className="px-2 py-3 text-left text-xs sm:text-sm font-semibold">Department</th>
                  <th className="hidden lg:table-cell px-2 py-3 text-left text-xs sm:text-sm font-semibold">Designation</th>
                  <th className="hidden lg:table-cell px-2 py-3 text-left text-xs sm:text-sm font-semibold">Number</th>
                  <th className="px-2 py-3 text-left text-xs sm:text-sm font-semibold">Actions</th>
                </tr>
              </thead>
              <tbody>
                {committees[activeCommitteeIndex].members.map((member) => (
                  <tr key={member._id} className="border-b">
                    <td className="px-2 py-3 text-left text-xs sm:text-sm font-semibold">{member.name}</td>
                    <td className="px-2 py-3 text-left text-xs sm:text-sm font-semibold">{member.department}</td>
                    <td className="hidden lg:table-cell px-2 py-3 text-left text-xs sm:text-sm font-semibold">{member.designation}</td>
                    <td className="hidden lg:table-cell px-2 py-3 text-left text-xs sm:text-sm font-semibold">{member.number}</td>
                    <td className="px-4 py-2 space-x-2">
                      <button
                        onClick={() => handleView(member._id)}
                        className="text-blue-500 hover:underline"
                      >
                        <FaEye />
                      </button>
                      <button
                        onClick={() => setEditMember(member)}
                        className="text-yellow-500 hover:underline"
                      >
                        <FaEdit />
                      </button>
                      <button
                        onClick={() => handleDelete(member._id)}
                        className="text-red-500 hover:underline"
                      >
                        <FaTrash />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {(viewMember || editMember) && (
        <div className="fixed inset-0 flex justify-center items-center bg-black bg-opacity-50">
          <div className="bg-white p-6 rounded-lg w-full sm:w-96 relative">
            <button
              onClick={closeModal}
              className="absolute top-2 right-2 text-gray-600"
            >
              <FaTimes />
            </button>

            {editMember && (
              <form onSubmit={handleEditSubmit}>
                <h3 className="text-2xl mb-4">Edit Member</h3>
                <label className="block text-sm font-medium text-gray-700">
                  Name
                  <input
                    type="text"
                    value={editMember.name}
                    onChange={(e) =>
                      setEditMember({ ...editMember, name: e.target.value })
                    }
                    className="mt-2 p-2 border rounded w-full"
                  />
                </label>

                <label className="block text-sm font-medium text-gray-700 mt-4">
                  Department
                  <input
                    type="text"
                    value={editMember.department}
                    onChange={(e) =>
                      setEditMember({
                        ...editMember,
                        department: e.target.value,
                      })
                    }
                    className="mt-2 p-2 border rounded w-full"
                  />
                </label>

                <label className="block text-sm font-medium text-gray-700 mt-4">
                  Designation
                  <input
                    type="text"
                    value={editMember.designation}
                    onChange={(e) =>
                      setEditMember({
                        ...editMember,
                        designation: e.target.value,
                      })
                    }
                    className="mt-2 p-2 border rounded w-full"
                  />
                </label>

                <label className="block text-sm font-medium text-gray-700 mt-4">
                  Number
                  <input
                    type="text"
                    value={editMember.number}
                    onChange={(e) =>
                      setEditMember({
                        ...editMember,
                        number: e.target.value,
                      })
                    }
                    className="mt-2 p-2 border rounded w-full"
                  />
                </label>

                <button
                  type="submit"
                  className="mt-4 px-4 py-2 bg-blue-500 text-white rounded w-full"
                >
                  Update Member
                </button>
              </form>
            )}

            {viewMember && (
              <div>
                <h3 className="text-2xl mb-4">View Member</h3>
                <p><strong>Name:</strong> {viewMember.name}</p>
                <p><strong>Department:</strong> {viewMember.department}</p>
                <p><strong>Designation:</strong> {viewMember.designation}</p>
                <p><strong>Number:</strong> {viewMember.number}</p>
              </div>
            )}
          </div>
        </div>
      )}

      <ToastContainer />
    </div>
  );
}
